import React, { useState, useRef, useEffect } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import LayerList from './components/LayerList';
import APIDocumentation from './components/APIDocumentation';
import Toast from './components/Toast';
import SearchFilter from './components/SearchFilter';
import Select from 'react-select';
import './App.css';

const REGION_OPTIONS = [
  { value: 'us-east-1', label: 'us-east-1: US East (N. Virginia)' },
  { value: 'us-east-2', label: 'us-east-2: US East (Ohio)' },
  { value: 'us-west-1', label: 'us-west-1: US West (N. California)' },
  { value: 'us-west-2', label: 'us-west-2: US West (Oregon)' },
  { value: 'eu-west-1', label: 'eu-west-1: Ireland' },
  { value: 'eu-west-2', label: 'eu-west-2: London' },
  { value: 'eu-west-3', label: 'eu-west-3: Paris' },
  { value: 'eu-central-1', label: 'eu-central-1: Frankfurt' },
  { value: 'eu-central-2', label: 'eu-central-2: Zurich' },
  { value: 'eu-north-1', label: 'eu-north-1: Stockholm' },
  { value: 'eu-south-1', label: 'eu-south-1: Milan' },
  { value: 'eu-south-2', label: 'eu-south-2: Spain' },
  { value: 'me-central-1', label: 'me-central-1: Middle East (UAE)' },
  { value: 'me-south-1', label: 'me-south-1: Middle East (Bahrain)' },
  { value: 'sa-east-1', label: 'sa-east-1: São Paulo' },
  { value: 'af-south-1', label: 'af-south-1: Africa (Cape Town)' },
  { value: 'ap-south-1', label: 'ap-south-1: Asia Pacific (Mumbai)' },
  { value: 'ap-south-2', label: 'ap-south-2: Asia Pacific (Hyderabad)' },
  { value: 'ap-northeast-1', label: 'ap-northeast-1: Asia Pacific (Tokyo)' },
  { value: 'ap-northeast-2', label: 'ap-northeast-2: Asia Pacific (Seoul)' },
  { value: 'ap-northeast-3', label: 'ap-northeast-3: Asia Pacific (Osaka)' },
  { value: 'ap-southeast-1', label: 'ap-southeast-1: Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'ap-southeast-2: Asia Pacific (Sydney)' },
  { value: 'ap-southeast-3', label: 'ap-southeast-3: Asia Pacific (Jakarta)' },
  { value: 'ap-southeast-4', label: 'ap-southeast-4: Asia Pacific (Melbourne)' },
  { value: 'ap-southeast-5', label: 'ap-southeast-5: Asia Pacific (Malaysia)' },
  { value: 'ca-central-1', label: 'ca-central-1: Canada (Central)' },
  { value: 'ca-west-1', label: 'ca-west-1: Canada (Calgary)' }
];

function App() {
  const [selectedRegion, setSelectedRegion] = useState({ value: 'us-east-1', label: 'US East (N. Virginia)' });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArchitecture, setSelectedArchitecture] = useState('All');
  const toastTimeoutRef = useRef(null);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const showToast = (message, type = 'success') => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    setToast({ show: true, message, type });
    
    // Auto-hide toast after 3 seconds
    toastTimeoutRef.current = setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  const hideToast = () => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast(prev => ({ ...prev, show: false }));
  };
  
  const handleRegionChange = (selectedOption) => {
    setSelectedRegion(selectedOption);
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>Layers for New Relic installation</h1>
        <p>Install or upgrade New Relic using these convenient layers!</p>
        <p>
          Most users will want to apply these layers automatically using the{' '}
          <a href="https://github.com/newrelic/newrelic-lambda-cli">New Relic Lambda CLI</a> or{' '}
          <a href="https://github.com/newrelic/serverless-newrelic-lambda-layers/">New Relic Serverless Plugin</a>.
        </p>
        <p>
          Our layer ARNs and layer API are published here for your convenience if manually applying
          layers to your functions or building custom automation tooling.
        </p>
      </header>
      
      <main>
        <div className="card">
          <Tabs className="custom-tabs">
            <TabList className="custom-tab-list">
              <Tab className="custom-tab">Layers</Tab>
              <Tab className="custom-tab">API</Tab>
            </TabList>

            <TabPanel className="custom-tab-panel">
              <div className="controls-row">
                <label htmlFor="region-select" className="control-label">Select your AWS Region</label>
                <Select 
                  inputId="region-select" 
                  value={selectedRegion} 
                  onChange={handleRegionChange} 
                  options={REGION_OPTIONS} 
                  classNamePrefix="react-select" 
                />
                  <SearchFilter 
                    searchTerm={searchTerm} 
                    onSearchChange={setSearchTerm}
                    selectedArchitecture={selectedArchitecture}
                    onArchitectureChange={setSelectedArchitecture}
                  />
              </div>
              <LayerList 
                region={selectedRegion.value} 
                showToast={showToast} 
                searchTerm={searchTerm} 
                onSearchChange={setSearchTerm}
                selectedArchitecture={selectedArchitecture}
              />
            </TabPanel>
            
            <TabPanel className="custom-tab-panel">
              <APIDocumentation />
            </TabPanel>
          </Tabs>
        </div>
      </main>
      
      {/* Toast notification at app level for proper viewport positioning */}
      <Toast 
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  );
}

export default App;
